import React, { memo, useMemo, useState } from "react";
import { Box } from "@mui/material";
import {
  BRAND,
  GREY,
  BASE,
  BORDER,
  SUCCESS,
  WARNING,
  PRIMARY,
  INDIGO,
  TEAL,
  ORANGE,
  PURPLE,
} from "../../../../../utils/constants/colors";
import { fontWeight, ICON_LABELS } from "../../../../../utils/constants/UI";
import IconComponent from "../../../../../components/Icon/IconComponent";
import TypographyInline from "../../../../../components/Typography/TypographyInline";
import { Button } from "../../../../../components/Buttons";
import { sharedBillingStyles as s } from "./billingUtils";
import BillingInviteMemberModal from "./BillingInviteMemberModal";

const PALETTE = [
  BRAND.PRIMARY,
  INDIGO.W_500,
  TEAL.W_500,
  ORANGE.W_500,
  PURPLE.W_500,
];

const INITIAL_MEMBERS = [
  {
    name: "Sarah Johnson",
    email: "sarah@acme.com",
    role: "Owner",
    status: "Active",
    creditsRemaining: 4200,
    creditsTotal: 5000,
  },
  {
    name: "Michael Chen",
    email: "michael@acme.com",
    role: "Member",
    status: "Active",
    creditsRemaining: 2750,
    creditsTotal: 5000,
  },
  {
    name: "Emily Davis",
    email: "emily@acme.com",
    role: "Member",
    status: "Active",
    creditsRemaining: 4980,
    creditsTotal: 5000,
  },
  {
    name: "James Wilson",
    email: "james@acme.com",
    role: "Member",
    status: "Active",
    creditsRemaining: 320,
    creditsTotal: 5000,
  },
  {
    name: "Alex Rivera",
    email: "alex@acme.com",
    role: "Member",
    status: "Pending",
    creditsRemaining: 0,
    creditsTotal: 5000,
  },
];

const initialsFor = (input) => {
  const source = (input || "").trim();
  if (!source) return "?";
  const parts = source.split(/[\s.@_-]+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return source.slice(0, 2).toUpperCase();
};

const colorFor = (key) => {
  let hash = 0;
  const str = key || "";
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) | 0;
  }
  return PALETTE[Math.abs(hash) % PALETTE.length];
};

const statusStyle = (status) =>
  status === "Active"
    ? { background: SUCCESS.W_50, color: SUCCESS.W_600 }
    : { background: WARNING.W_50, color: WARNING.W_600 };

const BillingSeatsSection = memo(({ packages = [] }) => {
  const [members, setMembers] = useState(INITIAL_MEMBERS);
  const [inviteOpen, setInviteOpen] = useState(false);

  const totalMembers = members.length;
  const activeMembers = members.filter((m) => m.status === "Active").length;
  const pendingMembers = members.filter((m) => m.status === "Pending").length;

  const handleInvite = ({ email, seatPlan }) => {
    const planCredits = seatPlan?.credits || 5000;
    setMembers((m) => [
      ...m,
      {
        name: email.split("@")[0].replace(/[._-]/g, " "),
        email,
        role: "Member",
        status: "Pending",
        creditsRemaining: planCredits,
        creditsTotal: planCredits,
      },
    ]);
  };

  const summaryItems = useMemo(
    () => [
      { label: "TOTAL MEMBERS", value: `${totalMembers}` },
      { label: "ACTIVE", value: `${activeMembers}` },
      { label: "PENDING", value: `${pendingMembers}`, accent: true },
    ],
    [totalMembers, activeMembers, pendingMembers],
  );

  return (
    <Box style={s.contentWrapper}>
      {/* Seats Summary Bar */}
      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "16px",
          padding: "16px 20px",
          background: BASE.BACKGROUND_LIGHT,
          borderRadius: "10px",
          border: `1px solid ${BORDER.LIGHT}`,
          flexWrap: "wrap",
        }}
      >
        <Box
          style={{
            display: "flex",
            alignItems: "center",
            gap: "20px",
            flexWrap: "wrap",
          }}
        >
          {summaryItems.map((item, idx) => (
            <React.Fragment key={item.label}>
              <Box>
                <TypographyInline
                  size="xs"
                  color={GREY.TERTIARY}
                  fontWeight={fontWeight.MEDIUM}
                  label={item.label}
                />
                <TypographyInline
                  size="md"
                  fontWeight={fontWeight.BOLD}
                  color={item.accent ? BRAND.PRIMARY : GREY.PRIMARY}
                  label={item.value}
                />
              </Box>
              {idx < summaryItems.length - 1 && (
                <Box
                  style={{
                    width: "1px",
                    height: "36px",
                    background: BORDER.DEFAULT,
                  }}
                />
              )}
            </React.Fragment>
          ))}
        </Box>
        <Box style={{ width: "fit-content" }}>
          <Button
            label="Invite Member"
            size="sm36"
            variant="primary"
            LeftIcon={ICON_LABELS.GROUPS}
            onClick={() => setInviteOpen(true)}
          />
        </Box>
      </Box>

      {/* Members Table */}
      <Box style={s.card}>
        <Box style={s.cardHeader}>
          <TypographyInline
            size="md"
            fontWeight={fontWeight.SEMIBOLD}
            color={GREY.PRIMARY}
            label="Team Members"
          />
          <TypographyInline
            size="xs"
            color={GREY.SECONDARY}
            label={`${totalMembers} ${totalMembers === 1 ? "member" : "members"}`}
          />
        </Box>

        {/* Table Header */}
        <Box
          style={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr 1.4fr",
            padding: "10px 16px",
            background: BASE.BACKGROUND_LIGHT,
            borderRadius: "6px",
            marginBottom: "4px",
          }}
        >
          {["MEMBER", "STATUS", "CREDITS LEFT"].map((col, idx) => (
            <TypographyInline
              key={idx}
              size="xs"
              fontWeight={fontWeight.MEDIUM}
              color={GREY.TERTIARY}
              label={col}
            />
          ))}
        </Box>

        {/* Rows */}
        {members.map((member, idx) => {
          const st = statusStyle(member.status);
          const isOwner = member.role === "Owner";
          const total = member.creditsTotal || 0;
          const left = member.creditsRemaining || 0;
          const pct = total > 0 ? Math.min((left / total) * 100, 100) : 0;
          const lowCredits = total > 0 && left / total < 0.15;
          const creditFill = lowCredits ? BRAND.PRIMARY : SUCCESS.W_500;
          return (
            <Box
              key={`${member.email}-${idx}`}
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1fr 1.4fr",
                padding: "14px 16px",
                alignItems: "center",
                borderBottom:
                  idx < members.length - 1
                    ? `1px solid ${BORDER.LIGHT}`
                    : "none",
                transition: "background 0.18s ease",
              }}
              sx={{
                "&:hover": {
                  background: BASE.BACKGROUND_LIGHT,
                },
              }}
            >
              {/* Avatar + Name */}
              <Box
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  minWidth: 0,
                }}
              >
                <Box
                  style={{
                    width: "36px",
                    height: "36px",
                    borderRadius: "50%",
                    background: colorFor(member.email),
                    color: BASE.WHITE,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "12px",
                    fontWeight: 700,
                    flexShrink: 0,
                  }}
                >
                  {initialsFor(member.name || member.email)}
                </Box>
                <Box style={{ minWidth: 0 }}>
                  <Box
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      flexWrap: "wrap",
                    }}
                  >
                    <TypographyInline
                      size="sm"
                      fontWeight={fontWeight.MEDIUM}
                      color={GREY.PRIMARY}
                      label={member.name}
                    />
                    {isOwner && (
                      <span
                        style={{
                          ...s.badge,
                          background: PRIMARY.W_50,
                          color: BRAND.PRIMARY,
                        }}
                      >
                        Owner
                      </span>
                    )}
                  </Box>
                  <TypographyInline
                    size="xs"
                    color={GREY.SECONDARY}
                    label={member.email}
                  />
                </Box>
              </Box>

              {/* Status */}
              <Box>
                <span style={{ ...s.badge, ...st }}>{member.status}</span>
              </Box>

              {/* Credits Left */}
              <Box
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "5px",
                  minWidth: 0,
                }}
              >
                <Box
                  style={{
                    display: "flex",
                    alignItems: "baseline",
                    gap: "4px",
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  <TypographyInline
                    size="sm"
                    fontWeight={fontWeight.SEMIBOLD}
                    color={lowCredits ? BRAND.PRIMARY : GREY.PRIMARY}
                    label={left.toLocaleString()}
                  />
                  <TypographyInline
                    size="xs"
                    color={GREY.TERTIARY}
                    label={`/ ${total.toLocaleString()}`}
                  />
                </Box>
                <Box
                  style={{
                    height: "6px",
                    background: BORDER.DISABLED,
                    borderRadius: "999px",
                    overflow: "hidden",
                    width: "100%",
                    maxWidth: "180px",
                  }}
                >
                  <Box
                    style={{
                      width: `${pct}%`,
                      height: "100%",
                      background: creditFill,
                      borderRadius: "999px",
                      transition: "width 0.22s ease",
                    }}
                  />
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Seat info note */}
      <Box
        style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "14px 18px",
          background: PRIMARY.W_50,
          borderRadius: "10px",
          border: `1px solid ${PRIMARY.W_100}`,
        }}
      >
        <IconComponent
          iconLabel={ICON_LABELS.INFO}
          style={{ color: BRAND.PRIMARY, fontSize: "20px", flexShrink: 0 }}
        />
        <TypographyInline
          size="sm"
          color={GREY.PRIMARY}
          label="Each new member is invited on a plan you choose. They get their own credit allowance and you're billed prorated for the rest of the cycle."
        />
      </Box>

      <BillingInviteMemberModal
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        packages={packages}
        currentSeats={totalMembers}
        onInvite={handleInvite}
      />
    </Box>
  );
});

BillingSeatsSection.displayName = "BillingSeatsSection";

export default BillingSeatsSection;
